import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import './DeviceList.css';
import './DeviceDetail.css';
import { API_BASE, authHeaders } from '../constants/api';
import { fetchListing } from '../utils/thingsboardEdition';
import { severityLabels } from '../constants/alarmConstants';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { updateAlarmStatusAPI } from '../utils/alarmActions';
import { formatDateDE } from '../utils/formatDate';
import { useAuth } from '../context/AuthContext';
import { Hinzufügen, Löschen, Öffnen, Abbrechen } from '../icons';
import { AlarmStatusDropdown } from '../UI-Elements';


function DonutChart({ active, inactive }) {
  const total = active + inactive;
  const cx = 160, cy = 150, r = 70, sw = 28;
  const circ = 2 * Math.PI * r;

  if (total === 0) {
    return (
      <svg viewBox="0 0 320 300" className="donut-chart" role="img" aria-label="Keine Geräte">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ccc" strokeWidth={sw} />
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize="13" fill="#888">Keine Geräte</text>
      </svg>
    );
  }

  const activeFrac = active / total;
  const inactiveFrac = inactive / total;
  const activeDash = activeFrac * circ;
  const inactiveDash = inactiveFrac * circ;

  const outerR = r + sw / 2 + 6;   
  const labelR = r + sw / 2 + 38;  

  const activeMidDeg  = (180 + activeFrac  * 180) % 360;
  const inactiveMidDeg = (180 + activeFrac * 360 + inactiveFrac * 180) % 360;

  function toXY(deg, radius) {
    const rad = deg * Math.PI / 180;
    return { x: cx + radius * Math.sin(rad), y: cy - radius * Math.cos(rad) };
  }

  const aLine  = toXY(activeMidDeg,  outerR);
  const aLabel = toXY(activeMidDeg,  labelR);
  const iLine  = toXY(inactiveMidDeg, outerR);
  const iLabel = toXY(inactiveMidDeg, labelR);

  return (
    <svg viewBox="0 0 320 300" className="donut-chart" role="img"
      aria-label={`Aktive Geräte: ${active}, Inaktive Geräte: ${inactive}`}>

      <g transform={`rotate(90, ${cx}, ${cy})`}>
        {/* Background ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#b0cfe0" strokeWidth={sw} />
        {/* Active segment – green */}
        {active > 0 && (
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke="#2d6a2d" strokeWidth={sw}
            strokeDasharray={`${activeDash} ${circ}`}
            strokeDashoffset={0}
            strokeLinecap="butt"
          />
        )}
        {/* Inactive segment – red */}
        {inactive > 0 && (
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke="#b83232" strokeWidth={sw}
            strokeDasharray={`${inactiveDash} ${circ}`}
            strokeDashoffset={-activeDash}
            strokeLinecap="butt"
          />
        )}
      </g>

      {/* Active label */}
      {active > 0 && (
        <g>
          <line x1={aLine.x} y1={aLine.y} x2={aLabel.x} y2={aLabel.y}
            stroke="#555" strokeWidth="1.5" />
          <rect x={aLabel.x - 48} y={aLabel.y - 13} width={96} height={26}
            rx={13} fill="white" stroke="#bbb" strokeWidth="1" />
          <text x={aLabel.x} y={aLabel.y + 5} textAnchor="middle"
            fontSize="13" fontFamily="Lato, sans-serif" fill="#1B1C3B">
            Aktiv ({active})
          </text>
        </g>
      )}

      {/* Inactive label */}
      {inactive > 0 && (
        <g>
          <line x1={iLine.x} y1={iLine.y} x2={iLabel.x} y2={iLabel.y}
            stroke="#555" strokeWidth="1.5" />
          <rect x={iLabel.x - 52} y={iLabel.y - 13} width={104} height={26}
            rx={13} fill="white" stroke="#bbb" strokeWidth="1" />
          <text x={iLabel.x} y={iLabel.y + 5} textAnchor="middle"
            fontSize="13" fontFamily="Lato, sans-serif" fill="#1B1C3B">
            Inaktiv ({inactive})
          </text>
        </g>
      )}
    </svg>
  );
}


function notifText(n) {
  return n.subject || n.title || n.text || n.name
    || (n.info && (n.info.description || n.info.subject))
    || (n.data && n.data.title) || '—';
}

function isAlarmNotification(n) {
  if (!n) return false;
  if (n.type && String(n.type).toLowerCase().includes('alarm')) return true;
  if (n.data && JSON.stringify(n.data).toLowerCase().includes('alarm')) return true;
  return false;
}


const Home = () => {
  const { authToken: token } = useAuth();
  const navigate = useNavigate();

  const [activeCount,   setActiveCount]   = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState('');

  const [alarms,        setAlarms]        = useState([]);
  const [alarmsLoading, setAlarmsLoading] = useState(true);
  const [alarmsError, setAlarmsError] = useState('');
  const [openAlarmStatusMenu, setOpenAlarmStatusMenu] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');

  const [alarmToDelete, setAlarmToDelete] = useState(null);
  const [showAlarmDeleteConfirm, setShowAlarmDeleteConfirm] = useState(false);
  const [deletingAlarm, setDeletingAlarm] = useState(false);
  const alarmDeleteRef = useRef(null);
  useFocusTrap(showAlarmDeleteConfirm, alarmDeleteRef);

  const [notifToDelete, setNotifToDelete] = useState(null);
  const [showNotifDeleteConfirm, setShowNotifDeleteConfirm] = useState(false);
  const [deletingNotif, setDeletingNotif] = useState(false);
  const notifDeleteRef = useRef(null);
  useFocusTrap(showNotifDeleteConfirm, notifDeleteRef);

  const [notifications,  setNotifications]  = useState([]);
  const [notifsLoading,  setNotifsLoading]  = useState(true);
  const [notifsError, setNotifsError] = useState('');

  const closeAlarmStatusMenu = useCallback(() => setOpenAlarmStatusMenu(null), []);
  useDropdownClose(openAlarmStatusMenu, closeAlarmStatusMenu, '.alarm-status-wrapper');

  // Load device counts
  useEffect(() => {
    if (!token) return;
    setDevicesLoading(true);
    fetchListing('deviceInfos/all', {
      pageSize: 1000, page: 0, sortProperty: 'name', sortOrder: 'ASC', includeCustomers: true,
    }, token)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        const devices = data.data || [];
        setActiveCount(devices.filter(d => d.active).length);
        setInactiveCount(devices.filter(d => !d.active).length);
        setDevicesError('');
      })
      .catch(err => setDevicesError(`Geräte konnten nicht geladen werden: ${err.message || 'Netzwerkfehler'}.`))
      .finally(() => setDevicesLoading(false));
  }, [token]);


  useEffect(() => {
    if (!token) return;
    setAlarmsLoading(true);
    fetch(`${API_BASE}/alarms?searchStatus=ANY&pageSize=20&page=0&sortProperty=createdTime&sortOrder=DESC&fetchOriginator=true`,
      { headers: authHeaders(token) })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        // Only show truly active alarms – CLEARED_UNACK/-ACK are resolved and have no label
        const active = (data.data || []).filter(
          a => a.status === 'ACTIVE_UNACK' || a.status === 'ACTIVE_ACK'
        );
        setAlarms(active.slice(0, 3));
        setAlarmsError('');
      })
      .catch(err => setAlarmsError(`Alarme konnten nicht geladen werden: ${err.message || 'Netzwerkfehler'}.`))
      .finally(() => setAlarmsLoading(false));
  }, [token]);


  useEffect(() => {
    if (!token) return;
    setNotifsLoading(true);
    fetch(`${API_BASE}/notifications?pageSize=20&page=0&sortProperty=createdTime&sortOrder=DESC&unreadOnly=false`,
      { headers: authHeaders(token) })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        const filtered = (data.data || []).filter(n => !isAlarmNotification(n));
        setNotifications(filtered.slice(0, 3));
        setNotifsError('');
      })
      .catch(err => setNotifsError(`Benachrichtigungen konnten nicht geladen werden: ${err.message || 'Netzwerkfehler'}.`))
      .finally(() => setNotifsLoading(false));
  }, [token]);


  async function ackAlarm(alarm) {
    await changeAlarmStatus(alarm, 'ack', 'ACTIVE_ACK');
  }

  async function clearAlarm(alarm) {
    await changeAlarmStatus(alarm, 'clear', 'CLEARED_ACK');
  }

  async function changeAlarmStatus(alarm, action, newStatus) {
    const alarmId = alarm.id?.id ?? alarm.id;
    try {
      await updateAlarmStatusAPI(alarmId, action, token);
      if (newStatus === 'CLEARED_ACK') {
        // Behobene Alarme aus der Liste entfernen – wir zeigen nur aktive
        setAlarms(prev => prev.filter(a => (a.id?.id ?? a.id) !== alarmId));
      } else {
        setAlarms(prev => prev.map(a =>
          (a.id?.id ?? a.id) === alarmId ? { ...a, status: newStatus } : a
        ));
      }
      setStatusType('success');
      setStatusMessage(newStatus === 'CLEARED_ACK' ? 'Alarm erfolgreich behoben.' : 'Alarm erfolgreich bestätigt.');
    } catch (err) {
      console.error('Alarmstatus konnte nicht geändert werden:', err);
      setStatusType('error');
      setStatusMessage('Fehler: Alarmstatus konnte nicht geändert werden.');
    } finally {
      setOpenAlarmStatusMenu(null);
      setTimeout(() => setStatusMessage(''), 4000);
    }
  }

  const openAlarmRule = useCallback(async (alarm) => {
    try {
      const params = new URLSearchParams({
        pageSize: '50', page: '0', types: 'ALARM',
        sortProperty: 'createdTime', sortOrder: 'DESC',
        textSearch: alarm.type || alarm.name || '',
      });
      const res = await fetch(`${API_BASE}/calculatedFields?${params}`, { headers: authHeaders(token) });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `HTTP ${res.status}`);
      }
      const ruleData = await res.json();
      const rules = ruleData.data || [];
      const originatorId = alarm?.originator?.id?.id ?? alarm?.originator?.id ?? alarm?.originator;
      const match = rules.find(r =>
        r.name === (alarm.type || alarm.name) &&
        (r.entityId?.id === originatorId || !originatorId)
      ) || rules.find(r => r.name === (alarm.type || alarm.name));
      if (match) navigate(`/alarm-rules/${match.id?.id}`);
    } catch { /* ignore */ }
  }, [token, navigate]);

  async function performDeleteAlarm() {
    if (!alarmToDelete) return;
    const alarmId = alarmToDelete.id?.id ?? alarmToDelete.id;
    setDeletingAlarm(true);
    try {
      const res = await fetch(`${API_BASE}/alarm/${alarmId}`,
        { method: 'DELETE', headers: authHeaders(token) });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `HTTP ${res.status}`);
      }
      setAlarms(prev => prev.filter(a => (a.id?.id ?? a.id) !== alarmId));
      setStatusType('success');
      setStatusMessage(`Alarm „${alarmToDelete.name}“ wurde gelöscht.`);
    } catch (err) {
      setStatusType('error');
      setStatusMessage(`Alarm konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`);
    } finally {
      setDeletingAlarm(false);
      setShowAlarmDeleteConfirm(false);
      setAlarmToDelete(null);
      setTimeout(() => setStatusMessage(''), 4000);
    }
  }

  async function performDeleteNotification() {
    if (!notifToDelete) return;
    const notifId = notifToDelete.id?.id ?? notifToDelete.id;
    setDeletingNotif(true);
    try {
      const res = await fetch(`${API_BASE}/notification/${notifId}`,
        { method: 'DELETE', headers: authHeaders(token) });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `HTTP ${res.status}`);
      }
      setNotifications(prev => prev.filter(x => (x.id?.id ?? x.id) !== notifId));
      setStatusType('success');
      setStatusMessage('Benachrichtigung wurde gelöscht.');
    } catch (err) {
      setStatusType('error');
      setStatusMessage(`Benachrichtigung konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`);
    } finally {
      setDeletingNotif(false);
      setShowNotifDeleteConfirm(false);
      setNotifToDelete(null);
      setTimeout(() => setStatusMessage(''), 4000);
    }
  }

  return (
    <div className="home-container">
      <h1>Willkommen beim Open KI IoT Hub SH</h1>

      {statusMessage && (
        <div className={`status-bar${statusType === 'error' ? ' status-bar--error' : ''}`} role="status" aria-live="polite">
          <p>{statusMessage}</p>
        </div>
      )}

      <div className="home-grid">

        {/* ── Left card: active devices ── */}
        <div className="home-card-wrapper">
          <p className="home-card-label">Aktive Endgeräte</p>
          <div className="home-card home-card-devices">
            {/* Button styled exactly like DeviceDetail toolbar buttons */}
            <div className="device-actions home-device-actions">
              <button
                className="action-buttons"
                onClick={() => navigate('/add-device')}
                aria-label="Neues Endgerät hinzufügen"
                title="Neues Endgerät hinzufügen"
              >
                <div className="action-button-icon"><Hinzufügen /></div>
                <div>Neues Endgerät</div>
              </button>
            </div>
            {devicesLoading
              ? <p className="home-loading">Lade Geräte…</p>
              : devicesError
              ? <p role="alert" className="home-error">{devicesError}</p>
              : <DonutChart active={activeCount} inactive={inactiveCount} />
            }
          </div>
        </div>

        {/* ── Right card: alarms & notifications ── */}
        <div className="home-card-wrapper">
          <p className="home-card-label">Alarme und Benachrichtigungen</p>
          <div className="home-card home-card-alarms">

            {/* ── Alarme ── */}
            <h2 className="home-section-title">Alarme</h2>
            {alarmsLoading ? (
              <p className="home-loading">Lade Alarme…</p>
            ) : alarmsError ? (
              <p role="alert" className="home-error">{alarmsError}</p>
            ) : alarms.length === 0 ? (
              <p className="home-empty">Keine aktiven Alarme</p>
            ) : (
              alarms.map(a => {
                const alarmId = a.id?.id ?? a.id;
                return (
                  <div key={alarmId} className="list-item alarm-item">
                    <div className="alarm-main">
                      <span className="alarm-name">{a.name}</span>
                      {a.severity && (
                        <span className={`severity-badge ${a.severity.toLowerCase()}`}>
                          {severityLabels[a.severity] || a.severity}
                        </span>
                      )}
                    </div>

                    <div className="actions" style={{ alignItems: 'center' }}>
                      {/* date */}
                      <span style={{ fontSize: 14, color: '#555', whiteSpace: 'nowrap' }}>
                        {formatDateDE(a.startTs || a.createdTime)}
                      </span>

                      {/* status dropdown */}
                      <AlarmStatusDropdown
                        alarmId={alarmId}
                        status={a.status}
                        isOpen={openAlarmStatusMenu === alarmId}
                        onToggle={() => setOpenAlarmStatusMenu(openAlarmStatusMenu === alarmId ? null : alarmId)}
                        onAck={() => ackAlarm(a)}
                        onClear={() => clearAlarm(a)}
                        onClose={() => setOpenAlarmStatusMenu(null)}
                      />

                      {/* open */}
                      <button className="icon-button open" title="Alarmregel öffnen" aria-label={`Alarmregel ${a.name} öffnen`}
                        onClick={() => openAlarmRule(a)}>
                        <span className="action-button-icon" aria-hidden="true"><Öffnen /></span>
                      </button>

                      {/* delete */}
                      <button className="icon-button delete" title="Alarm löschen" aria-label={`Alarm ${a.name} löschen`}
                        onClick={() => { setAlarmToDelete(a); setShowAlarmDeleteConfirm(true); }}>
                        <span className="action-button-icon" aria-hidden="true"><Löschen /></span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            {/* ── Benachrichtigungen ── */}
            <h2 className="home-section-title" style={{ marginTop: 16 }}>Benachrichtigungen</h2>
            {notifsLoading ? (
              <p className="home-loading">Lade Benachrichtigungen…</p>
            ) : notifsError ? (
              <p role="alert" className="home-error">{notifsError}</p>
            ) : notifications.length === 0 ? (
              <p className="home-empty">Keine Benachrichtigungen</p>
            ) : (
              notifications.map(n => {
                const notifId = n.id?.id ?? n.id;
                return (
                  <div key={notifId} className="list-item">
                    <div className="alarm-main">
                      <span className="alarm-name">{notifText(n)}</span>
                    </div>
                    <div className="actions" style={{ alignItems: 'center' }}>
                      <span style={{ fontSize: 14, color: '#555', whiteSpace: 'nowrap' }}>
                        {formatDateDE(n.createdTime || n.ts || n.tsCreated)}
                      </span>
                      <button className="icon-button delete" title="Benachrichtigung löschen"
                        aria-label="Benachrichtigung löschen"
                        onClick={() => { setNotifToDelete(n); setShowNotifDeleteConfirm(true); }}>
                        <span className="action-button-icon" aria-hidden="true"><Löschen /></span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}

          </div>
        </div>

      </div>

      {showAlarmDeleteConfirm && alarmToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="home-delete-alarm-title">
          <div className="modal" ref={alarmDeleteRef}>
            <h2 id="home-delete-alarm-title">Alarm löschen?</h2>
            <p>
              Soll der Alarm <i>{alarmToDelete.name}</i> wirklich gelöscht werden? Diese Aktion ist <strong>unwiderruflich</strong>. Das Löschen dieses ausgelösten Alarms hat keine Auswirkung auf die zugrunde liegende Alarmregel oder zukünftige Alarme.
            </p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={performDeleteAlarm} disabled={deletingAlarm}>
                {deletingAlarm ? 'Löschen…' : <><Löschen /> Löschen bestätigen</>}
              </button>
              <button className="modal-secondary" onClick={() => { setShowAlarmDeleteConfirm(false); setAlarmToDelete(null); }} disabled={deletingAlarm}>
                <span aria-hidden="true"><Abbrechen /></span> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotifDeleteConfirm && notifToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="home-delete-notif-title">
          <div className="modal" ref={notifDeleteRef}>
            <h2 id="home-delete-notif-title">Benachrichtigung löschen?</h2>
            <p>Soll die Benachrichtigung wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={performDeleteNotification} disabled={deletingNotif}>
                {deletingNotif ? 'Löschen…' : <>🗑 Löschen bestätigen</>}
              </button>
              <button className="modal-secondary" onClick={() => { setShowNotifDeleteConfirm(false); setNotifToDelete(null); }} disabled={deletingNotif}>
                <span aria-hidden="true"><Abbrechen /></span> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;

